import moment from 'moment-timezone';
import React from 'react';
import ReactDOMServer from 'react-dom/server';

import { columnHelper } from '../../../containers/Table/Table3';
import { DATE_MONTH_DAY_YEAR, DATE_TIME_SPACE_12_UPPERCASE, NOT_ENROLLED_REASON, RPM_PATIENT_STATUS, TERMINATED_REASON, Timezones } from '../../../constants';
import Strings from '../../../Strings';
import { getSchedulesIcons, getVitalsIcons, lastActivityDisplay, makeValid, showStatus, toolTipForTable } from '../../../utils';
import { renderPrograms } from '../../../utils/cmsPrograms';
import { VITALS } from '../../../utils/readings';
import { convertCelsiusToFahrenheit, convertKilogramToPound, GlucoseUnitConverter, GLUCOSE_UNITS, roundToDecimal, TEMPERATURE_UNITS } from '../../../utils/unitConverters';
import { canViewCohorts, ORG_FEATURES, orgFeatureEnabled } from '../../../utils/userPermissions';
import { getAlertsValue, eventTypeRenderer, typeRenderer } from '../../Alerts/helpers';

// Helper functions for column cells
const formatAlertNumber = (value, condition) => {
  switch (condition.field) {
    case VITALS.WEIGHT:
      return { value: roundToDecimal(convertKilogramToPound(value), 1), unit: Strings.unitsEnum.lbs };
    case VITALS.TEMPERATURE:
      return { value: roundToDecimal(convertCelsiusToFahrenheit(value), 1), unit: TEMPERATURE_UNITS.f };
    case VITALS.SUGAR_LEVEL:
      return { value: roundToDecimal(GlucoseUnitConverter(value, condition.unit, GLUCOSE_UNITS.mg_dl), 1), unit: GLUCOSE_UNITS.mg_dl };
    case VITALS.OXYGEN_SATURATION:
      return { value, unit: '%' };
    case VITALS.HEART_RATE:
      return { value, unit: Strings.bpm_unit };
    case VITALS.PM25:
      return { value, unit: 'µg/m³' };
    case VITALS.PROTHROMBIN_TIME:
      return { value, unit: 's' };
    default:
      return { value };
  }
};

const renderAlertCondition = (condition, alert) => {
  if (condition.field === 'missing_mandatory_tasks' || condition.field === 'missing_cpt') {
    const title = condition.field === 'missing_cpt' ? Strings.missingCPTCodes : Strings.missingMandatoryTasks;
    return <><span className="patient-alerts-tooltip__value">{title}</span>{alert.extended_items?.length > 0 && <>: {alert.extended_items.join(', ')}</>}</>;
  }

  if (condition.field === 'missed_doses') {
    return <><span className="patient-alerts-tooltip__value">{condition.actual_value}</span>{alert.extended_items?.length > 0 && <> {alert.extended_items.join(', ')}</>}</>;
  }

  const actualValue = Number(condition.actual_value);
  if (condition.actual_value == null || !Number.isFinite(actualValue)) {
    const { value, tooltip } = getAlertsValue([condition], alert.extended_items);
    const formatted = toolTipForTable(value, tooltip);
    const displayValue = React.isValidElement(formatted) ? formatted.props.children : formatted;
    return <span className="patient-alerts-tooltip__value">{displayValue || String(condition.actual_value ?? condition.field)}</span>;
  }

  const actual = formatAlertNumber(actualValue, condition);
  const exceededMin = condition.min != null && actualValue < condition.min;
  const exceededMax = condition.max != null && actualValue > condition.max;
  const threshold = exceededMin || exceededMax
    ? formatAlertNumber(exceededMin ? condition.min : condition.max, condition)
    : null;
  const label = Strings.patientAlertFields[condition.field];
  return (
    <>
      {label && <>{label} </>}
      <span className="patient-alerts-tooltip__value">{actual.value}</span>{actual.unit && <> {actual.unit}</>}
      {threshold && <> {exceededMin ? '<' : '>'} {threshold.value}{threshold.unit && <> {threshold.unit}</>} ({exceededMin ? Strings.min : Strings.max})</>}
    </>
  );
};

const renderAlertValues = alert => {
  if (!alert.conditions?.length) {
    return alert.extended_items?.length ? <span className="patient-alerts-tooltip__value">{alert.extended_items.join(', ')}</span> : null;
  }
  return alert.conditions.map((condition, index) => (
    <React.Fragment key={`${condition.field}-${index}`}>
      {index > 0 && ' · '}
      {renderAlertCondition(condition, alert)}
    </React.Fragment>
  ));
};

const getAlerts = (alerts, activeAlerts) => {
  let alertsCount = 0;
  if (Array.isArray(activeAlerts)) {
    alertsCount = activeAlerts.length;
  } else if (alerts) {
    alertsCount = Object.values(alerts).filter(v => typeof v === 'number')?.length;
    alertsCount += alerts.schedule_missed_doses_alerts?.length || 0;
  }
  const tooltip = alertsCount > 0 && Array.isArray(activeAlerts)
    ? ReactDOMServer.renderToStaticMarkup(
      <div className="patient-alerts-tooltip">
        <table className="patient-alerts-tooltip__table">
          <tbody>
            {activeAlerts.map(alert => {
              const values = renderAlertValues(alert);
              const alertDate = alert.last_triggered_at || alert.created_at;
              return (
                <tr className="patient-alerts-tooltip__row" key={alert.id}>
                  <th scope="row" className="patient-alerts-tooltip__type">
                    {eventTypeRenderer(alert.event_type) || typeRenderer(alert.general_type)}
                  </th>
                  <td className="patient-alerts-tooltip__values">
                    {values && <>{values}</>}
                  </td>
                  <td className="patient-alerts-tooltip__date">
                    {alertDate && <>{moment(alertDate).format(DATE_TIME_SPACE_12_UPPERCASE)}</>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>,
    )
    : undefined;
  return (
    <div className="icon-and-text-container">
      <div
        className={`icon-container ${alertsCount > 0 ? 'alert' : 'tick grey'}`}
        data-tooltip-id={tooltip ? 'patient-alerts-tooltip' : undefined}
        data-tooltip-html={tooltip}
        data-testid={alertsCount > 0 ? 'patient-alert-icon' : undefined}
      />
    </div>
  );
};

const getBillableTime = time => {
  const time_m = moment.utc(time * 1000);
  const color = time < 60 * 60 ? 'red' : 'grey'; // BILLABLE_TIME_THRESHOLD
  return (
    <div className="icon-and-text-container">
      <div className={`billable-time-dot ${color}`} />
      {time_m.format('HH:mm:ss')}
    </div>
  );
};

const getInbox = hasUnreadConversations => (
  <div className="icon-and-text-container">
    <div className={`icon-container envelope ${hasUnreadConversations ? 'unread' : ''}`} />
  </div>
);

const getEnrollmentField = (patient, field) => {
  const enrollment = Object.values(patient?.patientEnrollment?.enrollments || {})[0];
  return enrollment?.[field];
};

// Base columns that are always shown
export const baseColumns = [
  columnHelper.accessor('pharmacyPatientId', {
    header: Strings.capPatient.patientId,
    cell: info => info.getValue(),
    size: 261,
  }),

  columnHelper.accessor('patientName', {
    header: Strings.capPatient.patientName,
    cell: info => makeValid(info.getValue()),
    size: 180,
  }),
  columnHelper.accessor('mrn', {
    header: Strings.capPatient.mrn,
    cell: info => info.getValue(),
    size: 120,
  })
];

// Conditional columns
export const orgNameColumn = columnHelper.accessor('orgName', {
  header: Strings.organization,
  cell: info => info.getValue(),
  size: 200,
});

export const statusColumn = columnHelper.accessor('status', {
  header: Strings.status,
  cell: info => Strings.capPatient.patientStatus[info.getValue()] || info.getValue() || '-',
  size: 150,
  enableSorting: false,
});

export const consentForProviderColumn = columnHelper.accessor('consentForProvider', {
  accessorFn: row => getEnrollmentField(row, 'consentForProvider'),
  header: Strings.status,
  cell: info => info.getValue() ? Strings.outreachStatusEnum[info.getValue()] : '-',
  size: 180,
});

export const consentForProviderDateColumn = columnHelper.accessor('consentForProviderSetAt', {
  accessorFn: row => getEnrollmentField(row, 'consentForProviderSetAt'),
  header: Strings.statusDate,
  cell: info => info.getValue() ? moment(info.getValue()).format(DATE_MONTH_DAY_YEAR) : '-',
  size: 155,
  enableSorting: false,
});

export const televisitDateColumn = columnHelper.accessor('enrollmentSchedules', {
  header: Strings.capPatient.scheduledTelevisits,
  cell: info => {
    const row = info.row.original;
    return row.enrollmentSchedules?.length > 0
      ? row.enrollmentSchedules.map(s => moment(s.enrollAt).format(DATE_TIME_SPACE_12_UPPERCASE)).join(', ')
      : '-';
  },
  size: 300,
  enableSorting: false,
});

export const portalAccessColumn = columnHelper.accessor('portalAccess', {
  header: Strings.portalAccess,
  cell: info => showStatus(info.getValue()),
  size: 150,
  enableSorting: false,
});

export const lastLoginColumn = columnHelper.accessor('last_login', {
  header: Strings.lastLogin,
  cell: info => lastActivityDisplay(info.getValue()),
  size: 150,
});

export const programColumn = columnHelper.accessor('patientEnrollment', {
  header: Strings.program,
  cell: info => renderPrograms(info.getValue()),
  size: 200,
});

// Date columns for different statuses
export const onboardedDateColumn = columnHelper.accessor('createdAt', {
  header: Strings.capPatient.onboardedDate,
  cell: info => info.getValue() ? moment(info.getValue()).format(DATE_MONTH_DAY_YEAR) : '-',
  size: 155,
});

export const registeredDateColumn = columnHelper.accessor('registeredAt', {
  header: Strings.capPatient.registeredDate,
  cell: info => info.getValue() ? moment(info.getValue()).format(DATE_MONTH_DAY_YEAR) : '-',
  size: 150,
});

const enrollmentDateCell = (patientEnrollment, dateField) => {
  const enrollment = Object.values(patientEnrollment?.enrollments || {})[0];
  if (!enrollment) {
    return '-';
  }
  const date = moment(enrollment[dateField]).format(DATE_MONTH_DAY_YEAR);
  if (!enrollment.missingMedicationSchedule) {
    return date;
  }
  return (
    <div className="icon-and-text-container">
      <div
        className="icon-container warning"
        data-tooltip-id="tooltip"
        data-tooltip-content={Strings.capPatient.noMedicationScheduleTooltip}
        data-testid="missing-medication-schedule-icon"
      />
      {date}
    </div>
  );
};

export const enrolledDateColumn = columnHelper.accessor('enrolledDate', {
  header: Strings.capPatient.enrolledDate,
  cell: info => enrollmentDateCell(info.getValue(), 'verifiedAt'),
  accessorFn: row => row.patientEnrollment,
  size: 150,
});

export const activatedDateColumn = columnHelper.accessor('activatedDate', {
  header: Strings.capPatient.activatedDate,
  cell: info => enrollmentDateCell(info.getValue(), 'enrollmentStart'),
  accessorFn: row => row.patientEnrollment,
  size: 150,
});

export const shipmentColumn = columnHelper.accessor('shippingStatus', {
  header: Strings.shipping.shippingStatus,
  cell: info => {
    const enrollments = info.getValue()?.enrollments;
    const shipmentStatus = Object.values(enrollments || {})?.[0]?.shipment?.status;
    return shipmentStatus ? Strings.shipping.shipmentStatuses[shipmentStatus] : '-';
  },
  accessorFn: row => row.patientEnrollment,
  size: 180,
  enableSorting: false,
});


export const deliveryMethodColumn = columnHelper.accessor('defaultShipmentMethod', {
  header: Strings.shipping.deliveryMethod,
  cell: info => {
    const enrollments = info.row.original?.patientEnrollment?.enrollments;
    const value = Object.values(enrollments || {})?.[0]?.shipmentMethod;
    return value ? (Strings.shipping.deliveryMethodLabels[value] || value) : '-';
  },
  size: 180,
  enableSorting: false,
});

// Activity and status columns
export const readingsColumn = columnHelper.accessor('lastReadings', {
  header: Strings.latestReadings,
  cell: info => {
    const row = info.row.original;
    return getVitalsIcons(info.getValue(), row.id);
  },
  size: 180,
  enableSorting: false,
});

export const schedulesColumn = columnHelper.accessor('todaysMedications', {
  header: Strings.todaysMedications,
  cell: info => {
    const row = info.row.original;
    return getSchedulesIcons(info.getValue(), row.lastReadings?.alerts?.schedule_missed_doses_alerts);
  },
  size: 180,
  enableSorting: false,
});

export const alertsColumn = columnHelper.accessor('alerts', {
  header: Strings.alerts,
  cell: info => getAlerts(info.getValue()?.alerts, info.row.original.activeAlerts),
  accessorFn: row => row.lastReadings,
  size: 149,
  enableSorting: false,
});

export const inboxColumn = columnHelper.accessor('unreadConversations', {
  header: Strings.messages.inbox,
  cell: info => getInbox(info.getValue()),
  size: 134,
  enableSorting: false,
});

export const billingTimeColumn = columnHelper.accessor('billable_time', {
  header: Strings.timeThisMonth,
  cell: info => getBillableTime(info.getValue()?.billable_time),
  accessorFn: row => row.lastReadings,
  size: 140,
  enableSorting: false,
});

// Patient details columns
export const kitIdColumn = columnHelper.accessor('kitIds', {
  header: Strings.kitId,
  cell: info => info.getValue()?.join(', ') || '-',
  size: 210,
  enableSorting: false,
});

export const cohortColumn = columnHelper.accessor('cohortName', {
  header: Strings.cohort,
  cell: info => info.getValue() || '-',
  size: 153,
});

export const facilityColumn = columnHelper.accessor('facilityName', {
  header: Strings.facility,
  cell: info => info.getValue() || '-',
  size: 155,
});

export const providerColumn = columnHelper.accessor('providers', {
  header: Strings.capPatient.provider,
  cell: info => {
    const providers = info.getValue();
    return providers?.length ? providers.join(', ') : '-';
  },
  size: 180,
  enableSorting: false,
});

export const phoneColumn = columnHelper.accessor('textPhoneNo', {
  header: Strings.capPatient.mobileNumber,
  cell: info => makeValid(info.getValue()),
  size: 190,
});

const manualSmsEnabledColumn = columnHelper.accessor('manualSmsEnabled', {
  header: Strings.capPatient.manualSmsEnabled,
  cell: info => showStatus(info.getValue() ? 'enabled' : 'disabled'),
  size: 190,
  enableSorting: false,
});

const smsNoticeEnabledColumn = columnHelper.accessor('smsNoticeEnabled', {
  header: Strings.capPatient.notificationsEnabled,
  cell: info => showStatus(info.getValue() ? 'enabled' : 'disabled'),
  size: 150,
  enableSorting: false,
});

export const emailColumn = columnHelper.accessor('email', {
  header: Strings.capPatient.email,
  cell: info => makeValid(info.getValue()),
  size: 200,
});

export const timezoneColumn = columnHelper.accessor('timezone', {
  header: Strings.timezone,
  cell: info => info.getValue() ? Timezones[info.getValue()] : '-',
  size: 220,
  enableSorting: false,
});

export const genderColumn = columnHelper.accessor('gender', {
  header: Strings.gender,
  cell: info => info.getValue() ? Strings.genders[info.getValue().toLowerCase()] : '-',
  size: 150,
  enableSorting: false,
});

export const organTypeColumn = columnHelper.accessor('transplantDetails', {
  header: Strings.capPatient.organType,
  cell: info => info.getValue()?.[0]?.organType || '-',
  size: 150,
  enableSorting: false,
});

export const insuranceColumn = columnHelper.accessor('insuranceName', {
  header: Strings.capPatient.insurance,
  cell: info => info.getValue()?.[0]?.insuranceName || '-',
  accessorFn: row => row.insurance,
  size: 117,
  enableSorting: false,
});

export const policyIdColumn = columnHelper.accessor('insurancePolicyId', {
  header: Strings.capPatient.policyId,
  cell: info => info.getValue()?.[0]?.policyId || '-',
  accessorFn: row => row.insurance,
  size: 250,
  enableSorting: false,
});

export const clinicContactColumn = columnHelper.accessor('centerDetails', {
  header: Strings.capPatient.clinicContact,
  cell: info => info.getValue()?.[0]?.clinicContact || '-',
  size: 200,
  enableSorting: false,
});

export const notEnrolledReasonColumn = columnHelper.accessor('notEnrolledReason', {
  header: Strings.reason,
  cell: info => {
    const reason = info.getValue();
    return reason ? Strings.capPatient.notEnrolledReason[reason] || reason : '-';
  },
  size: 180,
  enableSorting: false,
});

export const notEnrolledDateColumn = columnHelper.accessor('notEnrolledAt', {
  header: Strings.capPatient.notEnrolledDate,
  cell: info => info.getValue() ? moment(info.getValue()).format(DATE_MONTH_DAY_YEAR) : '-',
  size: 150,
});

export const disenrolledDateColumn = columnHelper.accessor('disenrolledAt', {
  header: Strings.capPatient.disenrolledDate,
  cell: info => info.getValue() ? moment(info.getValue()).format(DATE_MONTH_DAY_YEAR) : '-',
  size: 155,
});

export const disenrolledReasonColumn = columnHelper.accessor('terminatedReason', {
  header: 'Disenrolled Reason',
  cell: info => {
    const reason = info.getValue();
    if (!reason) return '-';
    const normalizedReason = typeof reason === 'string' ? reason : `${reason}`;
    const reasonKey = Object.keys(TERMINATED_REASON).find(key => TERMINATED_REASON[key] === normalizedReason);
    if (reasonKey && Strings.capPatient.terminatedReason?.[reasonKey]) {
      return Strings.capPatient.terminatedReason[reasonKey];
    }
    const normalizedLower = normalizedReason.toLowerCase();
    const lowerKey = Object.keys(Strings.capPatient.terminatedReason || {}).find(key => key.toLowerCase() === normalizedLower);
    return lowerKey ? Strings.capPatient.terminatedReason[lowerKey] : normalizedReason;
  },
  size: 190,
  enableSorting: false,
});

export const eligibilityColumn = columnHelper.accessor('eligibilitySummary', {
  header: Strings.insurancePlans,
  cell: info => {
    const eligibility = info.getValue();
    if (!eligibility) return '-';
    if (!eligibility.plans?.length) return '-';
    return toolTipForTable(eligibility.planCount, eligibility.plans.map(plan => {
      const start = moment(plan.coverageStartDate).format(DATE_MONTH_DAY_YEAR);
      const end = moment(plan.coverageEndDate).format(DATE_MONTH_DAY_YEAR);
      return `${plan.planName}: ${start} - ${end}`;
    }).join(', '));
  },
  size: 180,
  enableSorting: false,
});

// Outreach status columns for onboarded/verified patients with extended outreach status enabled
const outreachStatusColumn = columnHelper.accessor('outreachStatus', {
  accessorFn: patient => getEnrollmentField(patient, 'outreachStatus'),
  header: Strings.status,
  cell: info => info.getValue() ? (Strings.extendedOutreachStatusEnum[info.getValue()] || info.getValue()) : '-',
  size: 180,
  enableSorting: false,
});
const outreachStatusDateColumn = columnHelper.accessor('outreachSetAt', {
  accessorFn: patient => getEnrollmentField(patient, 'outreachSetAt'),
  header: Strings.stateChangedDate,
  cell: info => {
    return info.getValue() ? moment(info.getValue()).format(DATE_TIME_SPACE_12_UPPERCASE) : '-';
  },
  size: 180,
  enableSorting: false,
});
const nextOutreachColumn = columnHelper.accessor('nextOutreachAt', {
  accessorFn: patient => getEnrollmentField(patient, 'nextOutreachAt'),
  header: Strings.nextOutreachDate,
  cell: info => {
    return info.getValue() ? moment(info.getValue()).format(DATE_TIME_SPACE_12_UPPERCASE) : '-';
  },
  size: 180,
});

// Function to get columns based on status and permissions
export const getPatientColumns = (status, showTransplantDetails, showShippingDetails, showOrgs) => {
  const columns = [...baseColumns];

  // Add organization column if user has permission
  if (showOrgs) {
    columns.push(orgNameColumn);
  }

  // Date columns based on status
  switch (status) {
    case RPM_PATIENT_STATUS.onboarded:
      columns.push(onboardedDateColumn);
      break;
    case RPM_PATIENT_STATUS.verified:
      columns.push(registeredDateColumn);
      break;
    case RPM_PATIENT_STATUS.enrolled:
      columns.push(enrolledDateColumn);
      break;
    case RPM_PATIENT_STATUS.activated:
      columns.push(activatedDateColumn);
      break;
    case RPM_PATIENT_STATUS.notEnrolled:
      columns.push(notEnrolledDateColumn);
      columns.push(notEnrolledReasonColumn);
      break;
    case RPM_PATIENT_STATUS.ended:
      columns.push(disenrolledDateColumn);
      columns.push(disenrolledReasonColumn);
      break;
    default:
      break;
  }

  if (orgFeatureEnabled(ORG_FEATURES.use_outreach_status) && status === RPM_PATIENT_STATUS.verified) {
    columns.push(outreachStatusColumn, outreachStatusDateColumn, nextOutreachColumn);
  }
    if (orgFeatureEnabled(ORG_FEATURES.use_outreach_status) && status === RPM_PATIENT_STATUS.notEnrolled) {
    columns.push(outreachStatusColumn, outreachStatusDateColumn);
  }
  if (orgFeatureEnabled(ORG_FEATURES.inovalon_enabled)) {
    columns.push(eligibilityColumn);
  }
  // Activity
  columns.push(
    readingsColumn,
    schedulesColumn,
  );

  // Add status column if no specific status filter
  if (!status) {
    columns.push(statusColumn);
  }

  // Status-specific columns
  if (status === RPM_PATIENT_STATUS.onboarded && !orgFeatureEnabled(ORG_FEATURES.use_outreach_status)) {
    columns.push(consentForProviderColumn);
    // columns.push(consentForProviderDateColumn);
  }

  if (status === RPM_PATIENT_STATUS.verified) {
    columns.push(televisitDateColumn);
  }

  columns.push(
    providerColumn,
    facilityColumn,
    phoneColumn,
    manualSmsEnabledColumn,
    smsNoticeEnabledColumn,
    alertsColumn,
    insuranceColumn,
    policyIdColumn,
    emailColumn,
  );

  // Program column for enrolled/activated patients
  if ([RPM_PATIENT_STATUS.enrolled, RPM_PATIENT_STATUS.activated].includes(status)) {
    columns.push(programColumn);
  }

  // Shipping details for enrolled/activated patients
  if (showShippingDetails && [RPM_PATIENT_STATUS.enrolled, RPM_PATIENT_STATUS.activated].includes(status)) {
    columns.push(shipmentColumn);
    columns.push(deliveryMethodColumn);
  }

  // Activity columns
  columns.push(
    inboxColumn,
  );
  if (status === RPM_PATIENT_STATUS.activated) {
    // Show inbox only for activated patients
    columns.push(billingTimeColumn);
  }

  // Patient details columns
  if (canViewCohorts()) {
    columns.push(cohortColumn);
  }
  columns.push(
    timezoneColumn,
    genderColumn
  );

  // Transplant details if enabled
  if (showTransplantDetails) {
    columns.push(organTypeColumn);
  }

  // Clinic columns
  columns.push(
    clinicContactColumn
  );

  // Portal access columns
  if (orgFeatureEnabled(ORG_FEATURES.patient_portal_access) && status === RPM_PATIENT_STATUS.activated) {
    columns.push(portalAccessColumn, lastLoginColumn);
  }
  columns.push(
    kitIdColumn
  );

  return columns;
};

export const getPatientOverviewColumns = (showTransplantDetails, showOrgs) => {
  const columns = [...baseColumns];

  if (showOrgs) {
    columns.push(orgNameColumn);
  }
  if (orgFeatureEnabled(ORG_FEATURES.inovalon_enabled)) {
    columns.push(eligibilityColumn);
  }

  columns.push(
    readingsColumn,
    schedulesColumn,
    providerColumn,
    facilityColumn,
    phoneColumn,
    manualSmsEnabledColumn,
    smsNoticeEnabledColumn,
    alertsColumn,
    insuranceColumn,
    policyIdColumn,
    emailColumn,
    inboxColumn,
  );
  if (canViewCohorts()) {
    columns.push(cohortColumn);
  }
  columns.push(
    timezoneColumn,
    genderColumn,
  );

  if (showTransplantDetails) {
    columns.push(organTypeColumn);
  }

  columns.push(clinicContactColumn);
  return columns;
};

export default getPatientColumns;
